/**
 * AstrologyTab Component
 *
 * Dedicated tab for astrology context configuration.
 * Three independent features:
 * - User birth chart data (基础出生信息)
 * - Natal chart calculation (本命盘计算)
 * - Transit predictions (行运预测)
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { Star, User, Compass, Calendar, Zap, Clock, CalendarDays, CalendarRange } from 'lucide-react';
import { getConfigValue } from '@/config/fieldConfig';
import type { AgentConfiguration } from '@/types';

// Transit view presets for quick configuration
const TRANSIT_PRESETS = {
  daily: {
    label: '日运',
    icon: Zap,
    days: 1,
    view_scope: 'daily',
    planet_scope: 'all',
    description: '今日触发（含月亮精确相位）',
  },
  weekly: {
    label: '周运',
    icon: Clock,
    days: 7,
    view_scope: 'weekly',
    planet_scope: 'all_except_moon',
    description: '本周重点（排除月亮噪音）',
  },
  monthly: {
    label: '月运',
    icon: CalendarDays,
    days: 30,
    view_scope: 'monthly',
    planet_scope: 'with_personal',
    description: '月度主题（含太阳火星）',
  },
  yearly: {
    label: '年运',
    icon: CalendarRange,
    days: 365,
    view_scope: 'yearly',
    planet_scope: 'major',
    description: '年度大势（只看外行星）',
  },
} as const;

type PresetKey = keyof typeof TRANSIT_PRESETS;

// Output format options for transit display
const TRANSIT_FORMATS = {
  period: {
    label: '周期格式',
    description: '分组显示相位生命周期（开始/精确/结束）',
  },
  text: {
    label: '文本格式',
    description: '简洁的自然语言描述',
  },
  compact: {
    label: '符号格式',
    description: '紧凑的占星符号表示（节省 token）',
  },
} as const;

interface AstrologyTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const AstrologyTab: React.FC<AstrologyTabProps> = ({
  config,
  onChange,
  appContext: _appContext,
}) => {
  const isBirthChartEnabled = getConfigValue(config, 'hooks.astrology_plus.enabled') === true;
  const isNatalChartEnabled = getConfigValue(config, 'hooks.astrology_plus.include_natal_chart') === true;
  const isTransitEnabled = getConfigValue(config, 'hooks.astrology_plus.transit_enabled') === true;
  const includeRelationshipProfiles = getConfigValue(config, 'hooks.astrology_plus.include_relationship_profiles') ?? true;
  const natalInjectionMode = getConfigValue(config, 'hooks.astrology_plus.natal_injection_mode') ?? 'always';
  const transitDays = getConfigValue(config, 'hooks.astrology_plus.transit_days') ?? 30;
  const transitViewScope = getConfigValue(config, 'hooks.astrology_plus.transit_view_scope') as string | null;
  const transitPlanetScope = getConfigValue(config, 'hooks.astrology_plus.transit_planet_scope') as string ?? 'all_except_moon';
  const transitFormat = getConfigValue(config, 'hooks.astrology_plus.transit_format') as string ?? 'period';

  // Determine which preset is currently active (if any)
  const activePreset = Object.entries(TRANSIT_PRESETS).find(
    ([_, preset]) =>
      preset.days === transitDays &&
      preset.view_scope === transitViewScope &&
      preset.planet_scope === transitPlanetScope
  )?.[0] as PresetKey | undefined;

  // Apply a preset configuration
  const applyPreset = (presetKey: PresetKey) => {
    const preset = TRANSIT_PRESETS[presetKey];
    onChange('hooks.astrology_plus.transit_days', preset.days);
    onChange('hooks.astrology_plus.transit_view_scope', preset.view_scope);
    onChange('hooks.astrology_plus.transit_planet_scope', preset.planet_scope);
  };

  // Check if any astrology feature is enabled
  const hasAnyEnabled = isBirthChartEnabled || isNatalChartEnabled || isTransitEnabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">占星增强</h2>
          <p className="text-sm text-slate-500 mt-1">
            配置占星相关的用户上下文数据和预测功能（三项功能相互独立）
          </p>
        </div>
      </div>

      {/* User Birth Chart Toggle */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${isBirthChartEnabled ? 'bg-purple-100' : 'bg-slate-100'}`}>
              <User className={`w-6 h-6 ${isBirthChartEnabled ? 'text-purple-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">用户出生图数据</h3>
              <p className="text-sm text-slate-500 mt-1">
                在提示词中包含用户的出生图基础信息（出生日期、时间、地点等）
              </p>
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>注入内容示例：</strong>
                </p>
                <code className="text-xs text-slate-500 block mt-1">
                  用户出生于 1990年3月15日 14:30，北京
                </code>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange('hooks.astrology_plus.enabled', !isBirthChartEnabled)}
            className={`
              relative w-14 h-7 rounded-full transition-colors flex-shrink-0
              ${isBirthChartEnabled ? 'bg-purple-500' : 'bg-slate-300'}
            `}
          >
            <div
              className={`
                absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${isBirthChartEnabled ? 'translate-x-8' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <label className="block text-sm text-slate-700">
          完整占星上下文注入
          <select
            value={natalInjectionMode}
            onChange={(e) => onChange('hooks.astrology_plus.natal_injection_mode', e.target.value)}
            className="block mt-2 px-2 py-1 border border-slate-300 rounded text-sm"
          >
            <option value="always">始终注入</option>
            <option value="skill_gated">按技能匹配注入</option>
          </select>
        </label>
        <p className="text-sm text-slate-500">仅控制已启用的完整本命盘和行运；基础出生信息不受技能匹配影响。</p>
        {natalInjectionMode === 'skill_gated' && (
          <p className="text-xs text-slate-500">
            请在「技能」页为需要的技能分别勾选「自动加载完整本命盘」和「自动加载行运」。未勾选、无匹配或分类器关闭时不加载对应上下文。
          </p>
        )}
      </Card>

      {/* Natal Chart Calculation Toggle */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${isNatalChartEnabled ? 'bg-amber-100' : 'bg-slate-100'}`}>
              <Compass className={`w-6 h-6 ${isNatalChartEnabled ? 'text-amber-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">本命盘计算</h3>
              <p className="text-sm text-slate-500 mt-1">
                计算并注入完整的本命盘数据，包括行星位置、相位和格局
              </p>
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>注入内容示例：</strong>
                </p>
                <code className="text-xs text-slate-500 block mt-1 font-mono">
                  ☉♎26°H6 ☽♎0°H5 ☿♎13°H6 ♀♏20°H6 ♂♋21°H3...
                </code>
                <p className="text-xs text-slate-400 mt-2">
                  使用紧凑的占星符号格式，约 300-400 字节
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange('hooks.astrology_plus.include_natal_chart', !isNatalChartEnabled)}
            className={`
              relative w-14 h-7 rounded-full transition-colors flex-shrink-0
              ${isNatalChartEnabled ? 'bg-amber-500' : 'bg-slate-300'}
            `}
          >
            <div
              className={`
                absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${isNatalChartEnabled ? 'translate-x-8' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="relationship-profiles-label" className="font-medium text-slate-900">关系人档案摘要</h3>
            <p id="relationship-profiles-help" className="text-sm text-slate-500 mt-1">
              仅在基础出生信息或本命盘上下文执行时附带关系人摘要；不会独立启用占星增强。
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={includeRelationshipProfiles}
            aria-labelledby="relationship-profiles-label"
            aria-describedby="relationship-profiles-help"
            onClick={() => onChange('hooks.astrology_plus.include_relationship_profiles', !includeRelationshipProfiles)}
            className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${includeRelationshipProfiles ? 'bg-purple-500' : 'bg-slate-300'}`}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${includeRelationshipProfiles ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
      </Card>

      {/* Transit Predictions Toggle */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${isTransitEnabled ? 'bg-blue-100' : 'bg-slate-100'}`}>
              <Calendar className={`w-6 h-6 ${isTransitEnabled ? 'text-blue-600' : 'text-slate-400'}`} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-slate-900">行运预测</h3>
              <p className="text-sm text-slate-500 mt-1">
                加载预计算的行星过境事件，提供未来运势分析的上下文
              </p>
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>注入内容示例：</strong>
                </p>
                <code className="text-xs text-slate-500 block mt-1">
                  Transit ☉ conjunct natal ☽ (精确: 2024-01-15, 持续3天)
                </code>
                <p className="text-xs text-slate-400 mt-2">
                  包含相位开始/精确/结束时间、持续天数、当前状态
                </p>
              </div>

              {/* Transit Configuration */}
              {isTransitEnabled && (
                <div className="mt-4 space-y-4">
                  {/* Quick Preset Buttons */}
                  <div>
                    <label className="text-sm text-slate-600 block mb-2">快速预设:</label>
                    <div className="flex flex-wrap gap-2">
                      {(Object.entries(TRANSIT_PRESETS) as [PresetKey, typeof TRANSIT_PRESETS[PresetKey]][]).map(
                        ([key, preset]) => {
                          const Icon = preset.icon;
                          const isActive = activePreset === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => applyPreset(key)}
                              title={preset.description}
                              className={`
                                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium
                                transition-all duration-200
                                ${isActive
                                  ? 'bg-blue-500 text-white shadow-sm'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }
                              `}
                            >
                              <Icon className="w-4 h-4" />
                              {preset.label}
                            </button>
                          );
                        }
                      )}
                    </div>
                    {activePreset && (
                      <p className="text-xs text-slate-500 mt-1">
                        {TRANSIT_PRESETS[activePreset].description}
                      </p>
                    )}
                  </div>

                  {/* Advanced Configuration */}
                  <div className="pt-3 border-t border-slate-200">
                    <details className="group">
                      <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700">
                        高级配置
                      </summary>
                      <div className="mt-3 space-y-3 pl-1">
                        {/* Days */}
                        <div className="flex items-center gap-4">
                          <label className="text-sm text-slate-600 w-20">预测天数:</label>
                          <input
                            type="number"
                            min={1}
                            max={365}
                            value={transitDays}
                            onChange={(e) => onChange('hooks.astrology_plus.transit_days', parseInt(e.target.value) || 30)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                          />
                          <span className="text-xs text-slate-400">天 (1-365)</span>
                        </div>

                        {/* View Scope */}
                        <div className="flex items-center gap-4">
                          <label className="text-sm text-slate-600 w-20">视图范围:</label>
                          <select
                            value={transitViewScope || ''}
                            onChange={(e) => onChange('hooks.astrology_plus.transit_view_scope', e.target.value || null)}
                            className="px-2 py-1 border border-slate-300 rounded text-sm min-w-[140px]"
                          >
                            <option value="">无过滤</option>
                            <option value="daily">日运 (daily)</option>
                            <option value="weekly">周运 (weekly)</option>
                            <option value="monthly">月运 (monthly)</option>
                            <option value="yearly">年运 (yearly)</option>
                          </select>
                        </div>

                        {/* Planet Scope */}
                        <div className="flex items-center gap-4">
                          <label className="text-sm text-slate-600 w-20">行星范围:</label>
                          <select
                            value={transitPlanetScope}
                            onChange={(e) => onChange('hooks.astrology_plus.transit_planet_scope', e.target.value)}
                            className="px-2 py-1 border border-slate-300 rounded text-sm min-w-[140px]"
                          >
                            <option value="major">外行星 (木土天海冥)</option>
                            <option value="with_personal">+太阳火星</option>
                            <option value="all_except_moon">全部(排除月亮)</option>
                            <option value="all">全部(含月亮)</option>
                            <option value="moon">仅月亮</option>
                          </select>
                        </div>

                        {/* Output Format */}
                        <div className="flex items-center gap-4">
                          <label className="text-sm text-slate-600 w-20">输出格式:</label>
                          <select
                            value={transitFormat}
                            onChange={(e) => onChange('hooks.astrology_plus.transit_format', e.target.value)}
                            className="px-2 py-1 border border-slate-300 rounded text-sm min-w-[140px]"
                          >
                            {Object.entries(TRANSIT_FORMATS).map(([key, format]) => (
                              <option key={key} value={key}>
                                {format.label}
                              </option>
                            ))}
                          </select>
                          <span className="text-xs text-slate-400">
                            {TRANSIT_FORMATS[transitFormat as keyof typeof TRANSIT_FORMATS]?.description || ''}
                          </span>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange('hooks.astrology_plus.transit_enabled', !isTransitEnabled)}
            className={`
              relative w-14 h-7 rounded-full transition-colors flex-shrink-0
              ${isTransitEnabled ? 'bg-blue-500' : 'bg-slate-300'}
            `}
          >
            <div
              className={`
                absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${isTransitEnabled ? 'translate-x-8' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </Card>

      {/* Status Summary */}
      {hasAnyEnabled && (
        <Card className="p-4 bg-gradient-to-r from-purple-50 via-amber-50 to-blue-50 border-purple-100">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-purple-600" />
            <div>
              <h4 className="text-sm font-medium text-purple-900">当前启用的功能</h4>
              <div className="flex gap-2 mt-2 flex-wrap">
                {isBirthChartEnabled && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                    用户出生图
                  </span>
                )}
                {isNatalChartEnabled && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full">
                    本命盘计算
                  </span>
                )}
                {isTransitEnabled && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                    行运预测 ({transitDays}天)
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Help Text */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-2">占星功能说明</h4>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>• <strong>用户出生图数据</strong>：提供用户的基础出生信息，适用于一般性占星对话</li>
          <li>• <strong>本命盘计算</strong>：使用 astro-tool 实时计算完整的本命盘，适用于专业占星分析</li>
          <li>• <strong>行运预测</strong>：加载预计算的行星过境事件，提供未来运势分析的上下文</li>
          <li>• 三项功能相互独立，可根据需要任意组合启用</li>
        </ul>
      </Card>
    </div>
  );
};

export default AstrologyTab;
